/**
 * Color Utilities — Helper functions for audio-reactive color generation.
 */

/** Convert HSL to CSS string */
export function hsl(h: number, s: number, l: number, a = 1): string {
  return a < 1 ? `hsla(${h % 360}, ${s}%, ${l}%, ${a})` : `hsl(${h % 360}, ${s}%, ${l}%)`;
}

/** Lerp between two values */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Clamp a value between min and max */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Map a value from one range to another.
 * Example: mapRange(0.5, 0, 1, 100, 200) → 150
 */
export function mapRange(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  return outMin + ((value - inMin) / (inMax - inMin)) * (outMax - outMin);
}

/** Pre-built color palettes for visualizations */
export const COLOR_PALETTES = {
  votebeats: ['#6366F1', '#EC4899', '#8B5CF6', '#06B6D4', '#10B981'],
  fire: ['#FF0000', '#FF4500', '#FF8C00', '#FFD700', '#FFFFE0'],
  ocean: ['#001F3F', '#003366', '#0074D9', '#7FDBFF', '#39CCCC'],
  neon: ['#FF00FF', '#00FFFF', '#FF0080', '#00FF80', '#8000FF'],
  sunset: ['#FF6B6B', '#FFA07A', '#FFD93D', '#6BCB77', '#4D96FF'],
  mono: ['#FFFFFF', '#CCCCCC', '#999999', '#666666', '#333333'],
} as const;

export type PaletteName = keyof typeof COLOR_PALETTES;

/** Get a color from a palette by index (wraps around) */
export function paletteColor(palette: PaletteName, index: number): string {
  const colors = COLOR_PALETTES[palette];
  return colors[index % colors.length];
}
